import { App, Alert, Space } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { bulkDeleteBarriers, listBarrierIds } from '@/api/endpoints/barriers';
import BarriersLibraryFilters from '@/components/barriers/BarriersLibraryFilters';
import BarriersLibraryTable, {
  type BarriersLibraryTableHandle,
} from '@/components/barriers/BarriersLibraryTable';
import { useBarriersLibrary } from '@/hooks/useBarriersLibrary';
import { useBarrierWorkspace } from '@/stores/BarrierWorkspaceContext';
import { useRecomputeProgress } from '@/stores/RecomputeProgressContext';
import { useTileRevision } from '@/stores/TileRevisionContext';
import type { BarrierListItem, Bbox } from '@/types/barriers';
import { extractAxiosMessage } from '@/utils/axiosError';
import { chunk } from '@/utils/chunk';
import { notifyRecomputeEnqueued } from '@/utils/recomputeNotify';

const FOCUS_PADDING_FACTOR = 4;
const METERS_PER_DEGREE_LAT = 111_320;
// Mirrors the backend per-request cap (_BULK_MAX_IDS in barriers router).
const BULK_MAX_IDS = 500;

interface WorkspaceBarriersLibraryProps {
  readonly onRowReopen: () => void;
}

/**
 * Barriers-mode body of the workspace panel: filters + paginated table
 * + bulk-delete. Row click flies the map to the barrier and opens its
 * property sheet via the parent panel's selection hooks.
 */
export default function WorkspaceBarriersLibrary({
  onRowReopen,
}: WorkspaceBarriersLibraryProps) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { selectedId: selectedBarrierId, focusBarrier } = useBarrierWorkspace();
  const { bumpBarriers } = useTileRevision();
  const { notifyEnqueued } = useRecomputeProgress();

  const library = useBarriersLibrary();
  const { refresh: refreshLibrary, appliedFilters } = library;
  const [selectedKeys, setSelectedKeys] = useState<readonly number[]>([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selectAllBusy, setSelectAllBusy] = useState(false);
  const tableRef = useRef<BarriersLibraryTableHandle>(null);

  useEffect(() => {
    if (selectedBarrierId !== null) {
      tableRef.current?.scrollToBarrier(selectedBarrierId);
    }
  }, [selectedBarrierId]);

  const handleRowClick = useCallback(
    (item: BarrierListItem) => {
      onRowReopen();
      focusBarrier(item.id, _bboxForBarrier(item));
    },
    [focusBarrier, onRowReopen],
  );

  const handleSelectAllAcrossPages = useCallback(async () => {
    setSelectAllBusy(true);
    try {
      const result = await listBarrierIds({ ...appliedFilters });
      setSelectedKeys(result.barrier_ids);
    } catch (error) {
      message.error(
        extractAxiosMessage(error) ?? t('barriers.library.bulk.selectAllError'),
      );
    } finally {
      setSelectAllBusy(false);
    }
  }, [appliedFilters, message, t]);

  const handleClearSelection = useCallback(() => setSelectedKeys([]), []);

  const handleBulkDelete = useCallback(async () => {
    if (selectedKeys.length === 0) return;
    setBulkBusy(true);
    try {
      const batches = chunk(selectedKeys, BULK_MAX_IDS);
      const results = await Promise.all(
        batches.map((b) => bulkDeleteBarriers(b)),
      );
      const deleted = results.reduce((sum, r) => sum + r.deleted, 0);
      const enqueued = results.reduce(
        (sum, r) => sum + r.marked_stale,
        0,
      );
      message.success(t('barriers.library.bulk.deleteResult', { count: deleted }));
      notifyEnqueued(enqueued);
      notifyRecomputeEnqueued(message, t, enqueued);
      setSelectedKeys([]);
      bumpBarriers();
      refreshLibrary();
    } catch (error) {
      message.error(
        extractAxiosMessage(error) ?? t('barriers.library.bulk.deleteError'),
      );
    } finally {
      setBulkBusy(false);
    }
  }, [bumpBarriers, message, notifyEnqueued, refreshLibrary, selectedKeys, t]);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <BarriersLibraryFilters
        filters={library.filters}
        onChange={library.setFilters}
        onReset={library.resetFilters}
        onRefresh={library.refresh}
        disabled={library.status === 'loading'}
      />
      {library.status === 'error' && (
        <Alert
          type="error"
          showIcon
          message={t('barriers.library.loadError')}
          description={library.errorMessage ?? undefined}
        />
      )}
      <BarriersLibraryTable
        ref={tableRef}
        items={library.items}
        total={library.total}
        page={library.page}
        pageSize={library.pageSize}
        loading={library.status === 'loading'}
        selectedRowKeys={selectedKeys}
        focusedBarrierId={selectedBarrierId}
        bulkBusy={bulkBusy}
        selectAllBusy={selectAllBusy}
        sort={library.sort}
        onSortChange={library.setSort}
        onPageChange={library.setPage}
        onSelectionChange={setSelectedKeys}
        onSelectAllAcrossPages={handleSelectAllAcrossPages}
        onClearSelection={handleClearSelection}
        onRowClick={handleRowClick}
        onBulkDelete={handleBulkDelete}
      />
    </Space>
  );
}

function _bboxForBarrier(item: BarrierListItem): Bbox {
  const meters = item.radius_m * FOCUS_PADDING_FACTOR;
  const dLat = meters / METERS_PER_DEGREE_LAT;
  const cosLat = Math.cos((item.point.lat * Math.PI) / 180);
  const dLon = cosLat > 1e-6 ? dLat / cosLat : 0.5;
  return [
    item.point.lon - dLon,
    item.point.lat - dLat,
    item.point.lon + dLon,
    item.point.lat + dLat,
  ];
}
